/**
 * Live presence heartbeat.
 *
 * Pings POST /api/presence/heartbeat every 30s while the tab is visible.
 * Anonymous visitors get a stable random session_id stored in localStorage
 * so admins see the same anon row across page navigations.
 *
 * Mounted once at the top of <App />. Uses the route's pathname as the
 * "page" field so admins can see what hunters are currently viewing.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const SESSION_KEY = "loot_session_id";
const HEARTBEAT_MS = 30000;

function ensureSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export function usePresenceHeartbeat() {
  const location = useLocation();
  const pageRef = useRef(location.pathname);
  pageRef.current = location.pathname;

  useEffect(() => {
    const sessionId = ensureSessionId();
    const referrer = (document.referrer || "").slice(0, 300);

    const ping = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        await api.post("/presence/heartbeat", {
          session_id: sessionId,
          page: pageRef.current,
          referrer,
        });
      } catch {
        // Silently swallow — presence is best-effort.
      }
    };

    ping();
    const id = setInterval(ping, HEARTBEAT_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
}
